'use client'

import React, {
  MutableRefObject,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import mapboxgl, { FitBoundsOptions, LngLat, LngLatLike, PaddingOptions, Popup } from 'mapbox-gl'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import 'mapbox-gl/dist/mapbox-gl.css'
import { BasicHotelInfo, ExtendedHotelInfo } from '@/lib/backend-api'
import { formatHotelUrl, onMobileOrTablet, smallerOrEqualThanScreenWidth } from '@/lib/utils/utils'
import { Currency } from '@/types/Page'
import ConfigContextProvider, { ConfigContext } from '@/components/common/ConfigContext'
import { Domain, ExtendedDomainConfiguration } from '../../../domain-config/domain-config'
import { NotFoundHotelPopup } from '@/components/ui/search/NotFoundHotelPopup'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/virtual'
import SliderHotelCard from '@/components/ui/search/SliderHotelCard'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Virtual } from 'swiper'
import SwiperType from 'swiper/types/swiper-class'
import Modal from '@/components/common/Modal'
import classNames from 'classnames'
import { setSearchBounds } from '@/components/ui/wizard/WizardActions'
import {
  CLOSE_MAP_CLICK,
  MAP_DISPLAY_STATUS_CHANGED,
  PUBLIC_MAPBOX_TOKEN,
} from '@/lib/sharedConstants'
import { HotelTeaserBox } from '@/components/elements/hotel_tease_element/HotelTeaserBox'
import { withIcon } from '@/components/common/header/util'
import { fetchAllHotels } from '@/lib/frontent-api'
import { MapTypesLngLatBounds, MapTypesLngLatBoundsArray } from '@/components/ui/search/map-types'
import CloseButton from '@/components/elements/CloseButton'
import { SearchPageContext } from '@/components/pages/search-page/SearchPageContext'
import {
  HOTEL_FOCUSED_EVENT,
  HOTEL_UNFOCUSED_FOCUSED_EVENT,
} from '@/components/pages/search-page/SearchPage'
import { placeWithinSearchRadius } from '@/lib/mapUtils'
import { createRoot } from 'react-dom/client'
import { useTranslations } from 'next-intl'

const SUPPORTED_LANGUAGES = ['ar', 'en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ja', 'ko', 'vi']

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
mapboxgl.accessToken = PUBLIC_MAPBOX_TOKEN

interface AppMapProps {
  whitelabel: boolean
  currency: Currency
  foundHotels: ExtendedHotelInfo[]
  domain: Domain
  searchActive: boolean
  config: ExtendedDomainConfiguration
  withNavigationControls: boolean
  bookmarksPage: boolean
}

const FOUND_HOTELS_ICON_TEXT_OFFSET = [0.25, -8.5]

const FOUND_SELECTED_HOTEL_SOURCE = 'found-hotels-selected-source'
const FOUND_HOTEL_SOURCE = 'found-hotels-source'
const NOT_FOUND_HOTELS_SOURCE = 'points'

const FOUND_SELECTED_HOTEL_LAYER_ID = 'found-hotels-selected'
const FOUND_HOTEL_LAYER_ID = 'found-hotels'
const FOUND_SELECTED_HOTELS_ICON_OFFSET = [0.25, -7.5]

const MAP_MESSAGE_STYLE =
  'absolute top-0 left-1/2 z-10 mt-[60px] -translate-x-1/2 rounded-md bg-white px-2 py-1 text-sm shadow-md custom-breakpoint-370:mt-[10px]'

function HotelTeaserPopup(props: {
  domain: Domain
  hotel: ExtendedHotelInfo
  callback: (div: HTMLDivElement) => void
  currency: Currency
  config: ExtendedDomainConfiguration
  t: (key: string) => string
}): ReactElement {
  return (
    <ConfigContextProvider config={props.config} domain={props.domain} eagerLoadImages={false}>
      <HotelTeaserBox
        reference={props.callback}
        priceLine1={props.hotel.PriceLine1}
        priceLine2={props.hotel.PriceLine2}
        discount={props.hotel.Discount}
        hotelName={props.hotel.HotelName}
        images={props.hotel.Images}
        url={formatHotelUrl(props.hotel.Url)}
        destinations={props.hotel.Destinations}
        hotelCode={props.hotel.HotelCode}
        hotelCategoryText={props.hotel.HotelCategoryText}
        annotation={props.hotel.Annotation}
        specialCategory={props.hotel.SpecialCategory}
        isKmwHotel={false}
        programName=""
        ratingText={props.hotel.RatingText}
        ratingValue={props.hotel.RatingValue}
        buttonLabel={props.t('mapPopupOpenHotel')}
        withinPopup={true}
        hotelCategoryStars={props.hotel.HotelCategoryStars}
      />
    </ConfigContextProvider>
  )
}

export default function AppMap(props: AppMapProps): ReactElement {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map: MutableRefObject<mapboxgl.Map | null> = useRef<mapboxgl.Map>(null)
  const [lng] = useState(9.648856884172124)
  const [lat] = useState(54.30949682179747)
  const [allHotels, setAllHotels] = useState<BasicHotelInfo[]>([])
  const foundHotelsPopup = useRef<Popup>()
  const [selectedHotelIndex, setSelectedHotelIndex] = useState<number | undefined>(
    onMobileOrTablet() ? 0 : undefined,
  )
  const [sliderMoved, setSliderMoved] = useState(false)
  const [mapMoved, setMapMoved] = useState(false)
  const fitBoundsAfterNewSearchResultInProgress = useRef<boolean>(false)
  const [focusedHotelCode, setFocusedHotelCode] = useState<string | undefined>()
  const swiperRef = useRef<SwiperType>()
  const [modal, _setModal] = useState<ReactElement | undefined>()
  const [mapReady, setMapReady] = useState(false)
  const [notFoundHotelPopup, setNotFoundHotelPopup] = useState<ReactElement>()
  const [priceTagLoaded, setPriceTagLoaded] = useState(false)
  const [priceTagSelectedLoaded, setPriceTagSelectedLoaded] = useState(false)
  const [hotelMarkerLoaded, setHotelMarkerLoaded] = useState(false)
  const config = useContext(ConfigContext)!
  const [slidesPerView, setSlidesPerView] = useState(1)
  const { setDisplayMap } = useContext(SearchPageContext)
  const t = useTranslations('SearchPage')

  useEffect(() => {
    function updateSlidesPerView(): void {
      setSlidesPerView(window.innerWidth / getCardWidth())
    }

    updateSlidesPerView()

    window.addEventListener('resize', updateSlidesPerView)

    return () => {
      window.removeEventListener('resize', updateSlidesPerView)
    }
  }, [])

  useEffect(() => {
    function hidePopups(): void {
      if (!onMobileOrTablet() || !foundHotelsPopup.current) {
        return
      }

      foundHotelsPopup.current.remove()
    }

    window.addEventListener('resize', hidePopups)

    return () => {
      window.removeEventListener('resize', hidePopups)
    }
  }, [])

  useEffect(() => {
    fetchAllHotels(props.domain)
      .then((r) => setAllHotels(r))
      .catch(() => {
        console.log('Fetching Hotels Failed')
      })
  }, [])

  useEffect(() => {
    addNewHotelsToAllHotels(props.foundHotels, allHotels)
  }, [props.foundHotels, allHotels])

  useEffect(() => {
    if (!hotelMarkerLoaded) {
      return
    }
    // drawDots(notFoundHotels(props.foundHotels, allHotels))
    updateNotFountHotelsLayer(notFoundHotels(props.foundHotels, allHotels))
  }, [props.foundHotels, allHotels, mapReady, hotelMarkerLoaded])

  useEffect(() => {
    if (!map.current || !priceTagLoaded) {
      return
    }
    // drawFoundHotels(props.foundHotels)

    updateFoundHotelsLayer(props.foundHotels, true)
  }, [props.foundHotels, mapReady, priceTagLoaded])

  useEffect(() => {
    if (!map.current || !priceTagSelectedLoaded) {
      return
    }

    selectFoundHotel(
      selectedHotelIndex === undefined ? undefined : props.foundHotels[selectedHotelIndex],
    )
  }, [selectedHotelIndex, props.foundHotels, priceTagSelectedLoaded])

  useEffect(() => {
    if (!map.current || !priceTagSelectedLoaded) {
      return
    }
    selectFoundHotel(props.foundHotels.find((h) => h.HotelCode === focusedHotelCode))
  }, [focusedHotelCode, props.foundHotels, priceTagSelectedLoaded])

  useEffect(() => {
    setSliderMoved(false)
  }, [props.foundHotels])

  useEffect(() => {
    if (!sliderMoved) {
      return
    }

    zoomIntoView()
  }, [selectedHotelIndex, focusedHotelCode])

  useEffect(() => {
    window.addEventListener(
      HOTEL_FOCUSED_EVENT,
      (
        e: CustomEvent<{
          hotelCode: string
        }>,
      ) => {
        setFocusedHotelCode(e.detail.hotelCode)
      },
    )
  }, [])

  useEffect(() => {
    window.addEventListener(HOTEL_UNFOCUSED_FOCUSED_EVENT, () => {
      setFocusedHotelCode(undefined)
    })
  }, [])

  function setModal(modal: ReactElement | undefined): void {
    setNotFoundHotelPopup(undefined)
    _setModal(modal)
  }

  function selectFoundHotel(extendedHotelInfo: ExtendedHotelInfo | undefined): void {
    if (!extendedHotelInfo) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      map.current.getSource(FOUND_SELECTED_HOTEL_SOURCE)?.setData({
        type: 'FeatureCollection',
        features: [],
      })
      return
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    map.current.getSource(FOUND_SELECTED_HOTEL_SOURCE)?.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          id: extendedHotelInfo.HotelCode,
          geometry: {
            type: 'Point',
            coordinates: [extendedHotelInfo.Longitude, extendedHotelInfo.Latitude],
          },
          properties: {
            price: extendedHotelInfo.PriceLine2,
            'text-color':
              extendedHotelInfo.HotelCode === getSelectedOrFocusedHotel()?.HotelCode
                ? '#ffffff'
                : '#5a5a5a',
            icon:
              extendedHotelInfo.HotelCode === getSelectedOrFocusedHotel()?.HotelCode
                ? 'price-tag-selected'
                : 'price-tag',
            priority:
              extendedHotelInfo.HotelCode === getSelectedOrFocusedHotel()?.HotelCode ? 1 : 2,
            hotel: extendedHotelInfo,
            index: 0,
          },
        },
      ],
    })
  }

  function addNewHotelsToAllHotels(
    foundHotels: ExtendedHotelInfo[],
    allHotels: BasicHotelInfo[],
  ): void {
    if (allHotels.length === 0) {
      return
    }
    const newHotels: ExtendedHotelInfo[] = []
    foundHotels.forEach((foundHotel) => {
      if (!allHotels.find((hotel) => hotel.HotelCode === foundHotel.HotelCode)) {
        newHotels.push(foundHotel)
      }
    })
    if (newHotels.length > 0) {
      setAllHotels([...allHotels, ...newHotels])
    }
  }

  function zoomIntoView(): void {
    const selectedHotel = getSelectedOrFocusedHotel()

    if (!selectedHotel || !map.current) {
      return
    }

    const currentMapBounds = map.current.getBounds()
    const hotelCoordinates = new LngLat(selectedHotel.Longitude, selectedHotel.Latitude)

    if (currentMapBounds.contains(hotelCoordinates)) {
      return
    }

    const mapBoundsWithSelectedHotel = currentMapBounds.extend(hotelCoordinates)
    map.current.fitBounds(mapBoundsWithSelectedHotel, getFitBoundsOptions())
  }

  function notFoundHotels(
    foundHotels: ExtendedHotelInfo[],
    allHotels: BasicHotelInfo[],
  ): BasicHotelInfo[] {
    const foundHotelCodes = foundHotels.map((foundHotel) => foundHotel.HotelCode)
    return allHotels.filter((hotel) => !foundHotelCodes.includes(hotel.HotelCode))
  }

  // // Helper to find correct position of markers
  // function drawDots(hotels: BasicHotelInfo[]): void {
  //   const createdMarkers = hotels.map((h) => {
  //     const el = document.createElement('div')
  //     const text = document.createElement('div')
  //     el.appendChild(text)
  //     el.className = 'bg-fuchsia-500 h-[1px] w-[1px] rounded-[50%]'
  //
  //     // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return
  //     return new mapboxgl.Marker(el, { anchor: 'bottom' })
  //       .setLngLat([h.Longitude, h.Latitude])
  //       .addTo(map.current)
  //   })
  //   // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  //   markers.current = [...markers.current, ...createdMarkers]
  // }

  function updateNotFountHotelsLayer(basicHotelInfos: BasicHotelInfo[]): void {
    if (!map.current) {
      return
    }
    const features = basicHotelInfos.map((basicHotelInfo) => ({
      // feature for Mapbox DC
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [basicHotelInfo.Longitude, basicHotelInfo.Latitude],
      },
      properties: {
        hotel: basicHotelInfo,
      },
    }))
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    map.current.getSource(NOT_FOUND_HOTELS_SOURCE)?.setData({
      type: 'FeatureCollection',
      features: features,
    })
  }

  function getSelectedOrFocusedHotel(): ExtendedHotelInfo | undefined {
    if (onMobileOrTablet()) {
      return props.foundHotels[selectedHotelIndex ?? 0]
    } else {
      return props.foundHotels.find((hotel) => hotel.HotelCode === focusedHotelCode)
    }
  }

  function getPadding(): PaddingOptions {
    return { bottom: onMobileOrTablet() ? 300 : 10, top: 85, left: 40, right: 40 }
  }

  function getFitBoundsOptions(): FitBoundsOptions {
    return {
      maxZoom: 9,
      padding: getPadding(),
    }
  }

  function updateFoundHotelsLayer(
    extendedHotelInfos: ExtendedHotelInfo[],
    foundHotelsChanged: boolean,
  ): void {
    if (!map.current) {
      return
    }

    const features = extendedHotelInfos.map((extendedHotelInfo, index) => ({
      type: 'Feature',
      id: extendedHotelInfo.HotelCode,
      geometry: {
        type: 'Point',
        coordinates: [extendedHotelInfo.Longitude, extendedHotelInfo.Latitude],
      },
      properties: {
        price: extendedHotelInfo.PriceLine2,
        'text-color':
          extendedHotelInfo.HotelCode === getSelectedOrFocusedHotel()?.HotelCode
            ? '#ffffff'
            : '#5a5a5a',
        icon:
          extendedHotelInfo.HotelCode === getSelectedOrFocusedHotel()?.HotelCode
            ? 'price-tag-selected'
            : 'price-tag',
        priority: extendedHotelInfo.HotelCode === getSelectedOrFocusedHotel()?.HotelCode ? 1 : 2,
        hotel: extendedHotelInfo,
        index: index,
      },
    }))
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    map.current.getSource(FOUND_HOTEL_SOURCE)?.setData({
      type: 'FeatureCollection',
      features: features,
    })

    if (extendedHotelInfos.length > 0 && foundHotelsChanged) {
      const allMarkers = new mapboxgl.LngLatBounds()
      extendedHotelInfos.forEach((info) =>
        allMarkers.extend(new LngLat(info.Longitude, info.Latitude)),
      )
      fitBoundsAfterNewSearchResultInProgress.current = true
      try {
        map.current.fitBounds(allMarkers, getFitBoundsOptions())
      } catch (e) {
        if (e instanceof Error) {
          console.error(e.message, e.stack)
        } else {
          console.log(e)
        }
      }
      setMapMoved(false)
    }
  }

  function createPopupContainerAndPutIntoDomToEnsureProperRendering(): HTMLDivElement {
    const popupContainer = document.createElement('div')
    popupContainer.className = 'font-sans invisible fixed top-0 left-0'
    window.document.body.prepend(popupContainer)
    return popupContainer
  }

  function showFoundHotelPopup(hotel: ExtendedHotelInfo, coordinates: LngLatLike): void {
    setNotFoundHotelPopup(undefined)

    const popupContainer = createPopupContainerAndPutIntoDomToEnsureProperRendering()

    function setCorrectHeightAndAttachToPopup(div: HTMLDivElement): void {
      if (!map.current) {
        return
      }

      popupContainer.className = 'font-sans'
      popupContainer.style.height = `${div.offsetHeight}px`
      popupContainer.style.width = `${div.offsetWidth}px`

      window.document.body.removeChild(popupContainer)

      foundHotelsPopup.current = new Popup({
        closeButton: false,
        className: 'found-popup',
        anchor: undefined,
        closeOnClick: true,
      })
        .setLngLat(coordinates)
        .setDOMContent(popupContainer)
        .addTo(map.current)

      map.current.on('mouseenter', FOUND_HOTEL_LAYER_ID, () => {
        if (!map.current) {
          return
        }

        map.current.getCanvas().style.cursor = 'pointer'
      })

      // Change it back to a pointer when it leaves.
      map.current.on('mouseleave', FOUND_HOTEL_LAYER_ID, () => {
        if (!map.current) {
          return
        }

        map.current.getCanvas().style.cursor = ''
      })
    }

    createRoot(popupContainer).render(
      <HotelTeaserPopup
        domain={props.domain}
        hotel={hotel}
        callback={setCorrectHeightAndAttachToPopup}
        currency={props.currency}
        config={props.config}
        t={t}
      />,
    )
  }

  function drawSelectedFoundHotelsLayer(): void {
    if (!map.current) {
      return
    }
    map.current.loadImage(
      `/images/${props.domain}/price-bubble_without-price-selected.png`,
      (error, image) => {
        if (!map.current) {
          return
        }
        if (error) throw error

        if (!image) {
          return
        }

        map.current.addImage('price-tag-selected', image, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          stretchX: [
            [13, 29],
            [41, 57],
          ],
          stretchY: [[20, 34]],
          content: [15, 10, 54, 30],
          pixelRatio: 1,
        })

        setPriceTagSelectedLoaded(true)
      },
    )
    map.current.addSource(FOUND_SELECTED_HOTEL_SOURCE, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    })

    // Add a symbol layer

    map.current.addLayer({
      id: FOUND_SELECTED_HOTEL_LAYER_ID,
      type: 'symbol',
      source: FOUND_SELECTED_HOTEL_SOURCE,
      paint: {
        'text-color': '#fff',
        'text-translate': FOUND_HOTELS_ICON_TEXT_OFFSET,
      },
      layout: {
        'icon-image': 'price-tag-selected',
        'icon-size': 1.1,
        'icon-text-fit': 'both',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-ignore-placement': true,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // 'icon-overlap': 'always',
        'text-padding': 0,
        'text-font': ['Inter Semi Bold', 'Inter Regular'],
        'text-field': ['get', 'price'],
        'text-anchor': 'bottom',
        'icon-offset': FOUND_SELECTED_HOTELS_ICON_OFFSET,
        'symbol-sort-key': ['get', 'priority'],
      },
    })

    map.current.on('click', FOUND_SELECTED_HOTEL_LAYER_ID, (e) => {
      if (onMobileOrTablet()) {
        if (!swiperRef.current || !e.features || !e.features[0]) {
          return
        }
        const feature = e.features[0]
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        swiperRef.current.slideTo(feature.properties?.index, 1000)
      } else {
        if (!e.features || !e.features[0]) {
          return
        }

        const feature = e.features[0]
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        const coordinates = e.features[0].geometry.coordinates.slice() as LngLatLike
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const hotel = JSON.parse(feature.properties?.hotel) as ExtendedHotelInfo
        showFoundHotelPopup(hotel, coordinates)
      }
    })
  }

  function drawFoundHotelsLayer(): void {
    if (!map.current) {
      return
    }
    map.current.loadImage('/images/price_bubble.png', (error, image) => {
      if (error) throw error

      if (!map.current || !image) {
        return
      }

      map.current.addImage('price-tag', image, {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        stretchX: [
          [13, 29],
          [41, 57],
        ],
        stretchY: [[20, 34]],
        content: [15, 10, 54, 30],
        pixelRatio: 1,
      })
      setPriceTagLoaded(true)
    })

    map.current.addSource(FOUND_HOTEL_SOURCE, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    })

    map.current.addLayer({
      id: FOUND_HOTEL_LAYER_ID,
      type: 'symbol',
      source: FOUND_HOTEL_SOURCE,
      paint: {
        'text-color': '#5a5a5a',
        'text-translate': FOUND_HOTELS_ICON_TEXT_OFFSET,
      },
      layout: {
        'icon-image': 'price-tag',
        'icon-text-fit': 'both',
        'icon-allow-overlap': true,
        'text-padding': 0,
        'text-font': ['Inter Semi Bold', 'Inter Regular'],
        'text-field': ['get', 'price'],
        'text-anchor': 'bottom',
        'icon-offset': FOUND_HOTELS_ICON_TEXT_OFFSET,
        'symbol-sort-key': ['get', 'priority'],
      },
    })

    map.current.on('click', FOUND_HOTEL_LAYER_ID, (e) => {
      if (onMobileOrTablet()) {
        if (!swiperRef.current || !e.features || !e.features[0]) {
          return
        }
        const feature = e.features[0]
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        swiperRef.current.slideTo(feature.properties?.index, 1000)
      } else {
        if (!e.features || !e.features[0]) {
          return
        }
        const feature = e.features[0]
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        const coordinates = e.features[0].geometry.coordinates.slice() as LngLatLike
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const hotel = JSON.parse(feature.properties?.hotel) as ExtendedHotelInfo
        showFoundHotelPopup(hotel, coordinates)
      }
    })
  }

  function closeModal(): void {
    if (!map.current) {
      return
    }

    setNotFoundHotelPopup(undefined)
    map.current.off('click', closeModal)
  }

  function showNotFoundHotelPopup(hotel: BasicHotelInfo, coordinates: LngLatLike): void {
    if (!map.current) {
      return
    }

    const popupContainer = document.createElement('div')
    const popupReactRoot = createRoot(popupContainer)

    popupContainer.style.height = '140px'

    if (smallerOrEqualThanScreenWidth(500)) {
      // popupContainer.style.height = '180px'
      map.current.on('click', closeModal)

      setNotFoundHotelPopup(
        <ConfigContextProvider domain={props.domain} config={props.config}>
          <Modal
            visible={true}
            close={closeModal}
            mobileFullscreen={false}
            noSroll={true}
            additionalClassName="mt-20 h-fit"
            classNameAdditionalBody={
              placeWithinSearchRadius(hotel.Longitude, hotel.Latitude) ? 'h-[192px]' : 'h-[171px]'
            }
          >
            <NotFoundHotelPopup hotel={hotel} domain={props.domain} withinModal={true} t={t} />
          </Modal>
        </ConfigContextProvider>,
      )
      return
    }

    popupContainer.style.width = smallerOrEqualThanScreenWidth(1720) ? '320px' : '380px'

    popupReactRoot.render(
      <ConfigContextProvider config={props.config} domain={props.config.domain}>
        <NotFoundHotelPopup hotel={hotel} domain={props.domain} t={t} />
      </ConfigContextProvider>,
    )

    foundHotelsPopup.current = new Popup({
      closeButton: false,
      maxWidth: '400px',
      className: 'not-found-popup',
      anchor: undefined,
      closeOnClick: true,
    })
      .setLngLat(coordinates)
      .setDOMContent(popupContainer)
      .addTo(map.current)

    map.current.on('mouseenter', 'points', () => {
      if (!map.current) {
        return
      }

      map.current.getCanvas().style.cursor = 'pointer'
    })

    // Change it back to a pointer when it leaves.
    map.current.on('mouseleave', 'points', () => {
      if (!map.current) {
        return
      }

      map.current.getCanvas().style.cursor = ''
    })
  }

  function drawNotFoundHotelsLayer(): void {
    if (!map.current) {
      return
    }

    map.current.loadImage('/images/hotel.png', (error, image) => {
      if (!map.current || !image) {
        return
      }
      map.current.addImage('hotel-marker', image)

      setHotelMarkerLoaded(true)
    })

    // Add a GeoJSON source with 2 points
    map.current.addSource(NOT_FOUND_HOTELS_SOURCE, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    })

    // Add a symbol layer
    map.current.addLayer({
      id: 'points',
      type: 'symbol',
      source: NOT_FOUND_HOTELS_SOURCE,
      layout: {
        'icon-image': 'hotel-marker',
        'icon-anchor': 'bottom',
        'symbol-sort-key': 100,
      },
    })

    map.current.on('click', 'points', (e) => {
      if (!e.features || !e.features[0]) {
        return
      }
      const feature = e.features[0]
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      const coordinates = e.features[0].geometry.coordinates.slice() as LngLatLike
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const hotel = JSON.parse(feature.properties?.hotel) as BasicHotelInfo

      showNotFoundHotelPopup(hotel, coordinates)
    })
  }

  function zoomedOrMoved(): void {
    if (fitBoundsAfterNewSearchResultInProgress.current) {
      fitBoundsAfterNewSearchResultInProgress.current = false
      return
    }

    setMapMoved(true)
  }

  function drawLayers(): void {
    if (!map.current) {
      return
    }

    map.current.on('load', () => {
      drawNotFoundHotelsLayer()
      drawFoundHotelsLayer()
      drawSelectedFoundHotelsLayer()
      setMapReady(true)
    })
  }

  useEffect(() => {
    if (map.current || !mapContainer.current) {
      return
    } // initialize map only once

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      style: 'mapbox://styles/fitreisen/clauuv37m00b914o6qr0ee80e',
      center: [lng, lat],
      zoom: 1,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      projection: 'mercator',
    })
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
    const language = new MapboxLanguage({
      defaultLanguage: SUPPORTED_LANGUAGES.includes(config.language) ? config.language : 'en', //for local name use 'mul
    })
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    map.current.addControl(language)

    // if (props.withNavigationControls) {
    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right')
    // }
    drawLayers()

    map.current.on('moveend', zoomedOrMoved)
  }, [mapContainer.current, map.current])

  useEffect(
    () =>
      window.addEventListener(MAP_DISPLAY_STATUS_CHANGED, () => {
        if (map.current) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
          setTimeout(() => map.current && map.current?.resize(), 50)
        }
      }),
    [],
  )

  function closeMap(): void {
    window.dispatchEvent(new CustomEvent(CLOSE_MAP_CLICK, {}))
  }

  function publishSearchBoundsUpdatedEvent(): void {
    if (!map.current) {
      return
    }
    setMapMoved(false)
    window.store?.dispatch(
      setSearchBounds(
        MapTypesLngLatBounds.fromArray(
          map.current.getBounds().toArray() as MapTypesLngLatBoundsArray,
        ),
      ),
    )
  }

  function newSlideSelected(activeIndex: number): void {
    setSelectedHotelIndex(activeIndex)
    setSliderMoved(true)
  }

  function getCardWidth(): number {
    return Math.min(window.innerWidth - 70, 340)
  }

  return (
    <>
      {modal && (
        <Modal
          visible={true}
          close={() => {
            setModal(undefined)
          }}
          mobileFullscreen={false}
          noSroll={true}
        >
          {modal}
        </Modal>
      )}
      <div
        className={classNames('relative left-0 right-0 h-full w-full text-4xl lg:sticky', {
          'modal-open': modal,
        })}
      >
        {mapMoved && (
          <button
            onClick={publishSearchBoundsUpdatedEvent}
            className={classNames(
              MAP_MESSAGE_STYLE,
              'modal-open:hidden map-container font-semibold text-primary',
            )}
          >
            {t('mapSearchWithinBounds')}
          </button>
        )}
        {!mapMoved && !props.searchActive && props.foundHotels.length === 0 && (
          <div className={MAP_MESSAGE_STYLE}>{t('noSearchResults')}</div>
        )}
        <div className="absolute right-0 top-0 z-10 hidden lg:block">
          <CloseButton
            onClick={() => setDisplayMap(false)}
            additionalClassNames="rounded-full bg-white m-3 cursor-pointer"
            padding="p-2"
          />
        </div>
        <div ref={mapContainer} className={classNames('mapboxgl-map h-full w-full')} />
        <button
          onClick={closeMap}
          className={withIcon(
            'before:icon-long-arrow-left',
            'modal-open:hidden absolute left-[10px] top-[10px] cursor-pointer rounded-md bg-white py-1.5 pl-9 pr-4 text-base font-medium text-[#6D6D6D] shadow-md before:absolute before:top-1/2 before:inline-flex before:-translate-x-[22px] before:-translate-y-1/2 lg:hidden',
            'before:h-[16px] before:w-[16px]',
          )}
        >
          {t('mapBackToListButton')}
        </button>
        <div
          className="modal-open:hidden hotel-slider fixed bottom-0 z-10 mb-6 lg:hidden"
          key={props.foundHotels.map((h) => h.HotelCode).join('')}
        >
          <Swiper
            slidesPerView={slidesPerView}
            centeredSlides={true}
            slideToClickedSlide={true}
            resistanceRatio={1}
            speed={280}
            touchRatio={1}
            spaceBetween={slidesPerView > 1.35 ? 30 : 15}
            modules={[Virtual]}
            virtual
            onSlideChange={(e) => {
              newSlideSelected(e.activeIndex)
            }}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            wrapperClass="h-full"
          >
            {props.foundHotels.map((hotel, index) => (
              <SwiperSlide key={hotel.HotelCode} virtualIndex={index}>
                <SliderHotelCard
                  key={hotel.HotelCode}
                  hotel={hotel}
                  domain={props.domain}
                  setModal={setModal}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {notFoundHotelPopup}
      </div>
    </>
  )
}
